import PropTypes from 'prop-types';
import { useState, useEffect, useContext } from 'react';
import PubSub from 'pubsub-js';

import RcmEventsSender from 'core/components/RcmEventsSender';
import Feed from 'core/components/Feed';
import UserContext from 'core/components/User';

import withBindProps from 'core/hocs/withBindProps';
import withTopic from 'core/hocs/withTopic';

import modelPropTypes, { topicAttributes } from 'core/utils/prop-types/model';
import requestHeadersPropTypes from 'core/utils/prop-types/request-headers';
import { getRecommendations } from 'core/utils/rcm';
import { getCookie } from 'core/utils/cookie-helper';
import { parseUrl } from 'core/utils/url-helper';

import { RECOMMENDER_IDS, PUBSUB_RECOMMENDER_CHANNEL, USER_ID_COOKIE } from 'core/site/constants';

import CardWrapper from './CardWrapper';


function toSwaggerScheme(recommendations) {
  if (!Array.isArray(recommendations)) return [];

  return recommendations
    .map((recommendation) => ({
      id: recommendation.item_id,
      type: 'topic',
      attributes: {
        topic_type: 'recommender',
        headline: recommendation.item_title,
        link: recommendation.item_url,
      },
      extra: {
        rcm_item: recommendation,
      },
      relationships: {
        image: {
          data: {
            id: `image${recommendation.item_id}`,
            type: 'image',
            attributes: {
              versions: {
                original: {
                  rel_url: parseUrl(recommendation.item_image).pathname,
                  size: 1,
                },
              },
            },
          },
        },
      },
    }));
}

function Recommender(props, context) {
  const [recommendations, setRecommendations] = useState([]);

  const user = useContext(UserContext);

  const {
    blockId,
    limit,
    renderer: Renderer,
    card,
    topic,
    itemClassName,
    ...rendererProps
  } = props;

  const {
    requestHeaders: {
      host,
    },
  } = context;

  const contextItemId = topic?.content.id;
  const rcmSessionId = topic?.rcmSessionId;

  const CardWithRcmEvents = withBindProps({
    blockId,
    card,
    contextItemId,
    className: itemClassName,
  })(CardWrapper);

  useEffect(() => {
    window[RECOMMENDER_IDS] = null;
    /**
     * Чтобы избежать лишних ререндеров в результате резолвинга пользователя после отрисовки,
     * мы привязываемся к updateUser в контексте пользователя и ожидаем получения информации
     */
    if (!user.resolved) return;

    getRecommendations({
      blockId,
      limit,
      item_id: contextItemId,
      user,
      host,
      xuid: getCookie(USER_ID_COOKIE),
      ...(rcmSessionId && { session_id: rcmSessionId }),
    })
      .then(response => {
        const normalizedRecommendations = toSwaggerScheme(response.data.recommendations);
        setRecommendations(normalizedRecommendations);
        window[RECOMMENDER_IDS] = normalizedRecommendations.map(({ id }) => id);
        PubSub.publish(PUBSUB_RECOMMENDER_CHANNEL);
      })
      .catch(e => {
        window[RECOMMENDER_IDS] = [];
        PubSub.publish(PUBSUB_RECOMMENDER_CHANNEL);
        console.error(e);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextItemId, blockId, limit, user]);

  if (!recommendations.length) return null;

  return (
    <RcmEventsSender blockinit={{ blockId, contextItemId }}>
      <Renderer
        content={recommendations}
        card={CardWithRcmEvents}
        {...rendererProps}
      />
    </RcmEventsSender>
  );
}

Recommender.defaultProps = {
  renderer: Feed,
  limit: 10,
};

Recommender.propTypes = {
  /** Компонент, который будет рендерить карточки (Feed, Drum, etc.) */
  renderer: PropTypes.func,

  /**
   * Карточка, которой рендерить топики рекомендалки.<br/>
   * Всегда обернута в RcmEventsSender
   */
  card: PropTypes.func.isRequired,

  /** Параметр для получения рекомендаций и отправки событий RCM. */
  blockId: PropTypes.string.isRequired,

  /** Ограничение на колличество получаемых рекомендаций */
  limit: PropTypes.number,

  /**
   * Карточки в этом компоненте оборачиваются в RcmEventsSender, который
   * добавляет пустой div в качестве оберки для нее.<br/>
   * С помощью этого свойства можно задать класс для этой обертки
   */
  itemClassName: PropTypes.string,

  /** @ignore */
  topic: PropTypes.shape({
    content: modelPropTypes(topicAttributes).isRequired,
    rcmSessionId: PropTypes.string,
  }),
};

Recommender.contextTypes = {
  requestHeaders: requestHeadersPropTypes,
};

const RecommenderWithHOCs = withTopic(Recommender);

RecommenderWithHOCs.displayName = 'Recommender';

export { Recommender as StorybookComponent };
export default RecommenderWithHOCs;
